// The shared shape of every table screen, sized to fit one phone screen
// without scrolling (user, 2026-09-27): a top bar, an optional band (the
// dealer's side), the play area that takes whatever height is left, and one
// control row with the bet and the actions together.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { betLadder, tableLocked, tableOptions, type GameSnapshot } from '../../engine/casino';
import { GAMES } from '../../engine/games';
import { money, withCommas } from '../../format';
import type { GameActions } from '../../useGame';
import { BetControl, BuzzLine } from '../parts';
import { HowToPlay, markHowToSeen, seenHowTo } from './HowToPlay';

export interface TableProps {
  snapshot: GameSnapshot;
  actions: GameActions;
}

export function TableFrame({ snapshot, actions, children }: TableProps & { children: ReactNode }) {
  const run = snapshot.run!;
  const id = snapshot.activeGame!;
  const star = run.special === id;
  // During an animation the money is already settled; show it from before.
  const bankroll = snapshot.anim ? snapshot.anim.bankroll : run.bankroll;
  // How to play (user, 2026-09-27): the "?" opens it any time, and it opens
  // by itself the first time you sit at a game on this device.
  const [help, setHelp] = useState(false);
  useEffect(() => {
    if (seenHowTo(id)) return;
    markHowToSeen(id);
    setHelp(true);
  }, [id]);
  return (
    <div className={`table-screen${snapshot.anim ? ' skippable' : ''}`}>
      {/* While something plays, an invisible layer over the whole table
          screen takes any tap and skips (user, 2026-09-27): even over a
          disabled button, which would swallow the tap itself, and never a
          stray Deal underneath. */}
      {snapshot.anim && <button type="button" className="skip-layer" onClick={actions.skip} aria-label="Tap to skip" />}
      <header className="table-top">
        <div className="table-top-row">
          <button className="icon-btn" onClick={actions.back} disabled={tableLocked(snapshot)} aria-label="Leave table">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
          </button>
          <h1>
            {GAMES[id].name}
            {star && <span className="star-mark" aria-label="House Special"> ★</span>}
            <button type="button" className="help-btn" onClick={() => setHelp(true)} aria-label={`How to play ${GAMES[id].name}`}>
              ?
            </button>
          </h1>
          <span className="table-bankroll">{money(bankroll)}</span>
        </div>
        <div className="table-sub-row">
          <BuzzLine bar={run.bar} />
          {/* One fixed-size slot that's always there, so the header never
              changes and the table never jumps (user, 2026-09-27): a "Tap to
              skip" hint while something plays (a tap anywhere skips), Call
              waiter between plays, disabled mid-play. */}
          {snapshot.anim ? (
            <span className="waiter-btn skip" aria-live="polite">
              Tap to skip
            </span>
          ) : (
            <button className="waiter-btn" onClick={() => actions.tableOption('waiter')} disabled={!tableOptions(snapshot).some((o) => o.id === 'waiter')}>
              Call waiter
            </button>
          )}
        </div>
      </header>
      {children}
      <JackpotSplash snapshot={snapshot} />
      {help && <HowToPlay id={id} floor={run.floor} onClose={() => setHelp(false)} />}
    </div>
  );
}

/** The play area: takes the height that's left and never scrolls the page. */
export function Stage({ children, onClick, className = '' }: { children: ReactNode; onClick?: () => void; className?: string }) {
  return (
    <main className={`felt ${className}`} onClick={onClick}>
      {children}
    </main>
  );
}

/** The result of the last play and any notice (drinks, jackpot, bet lowered). */
export function StatusLine({ snapshot, result, tone }: { snapshot: GameSnapshot; result?: ReactNode; tone?: 'win' | 'loss' | '' }) {
  // It sits below the table's contents, which are anchored to the top, so it
  // coming and going never moves anything above it (user, 2026-09-27).
  if (snapshot.anim || (!result && !snapshot.notice)) return null;
  return (
    <div className="status">
      {result && <div className={`result ${tone ?? ''}`} role="status">{result}</div>}
      {snapshot.notice && <div className="notice">{withCommas(snapshot.notice)}</div>}
    </div>
  );
}

export interface ActionSpec {
  id: string;
  label: string;
  primary?: boolean;
  onClick?: () => void;
}

/** The bottom row: the bet control (between plays) and the action buttons. */
export function Controls({ snapshot, actions, extra, onAct, hide = [], primaryId }: TableProps & { extra?: ActionSpec[]; onAct?: () => void; hide?: string[]; primaryId?: string }) {
  const run = snapshot.run!;
  const id = snapshot.activeGame!;
  const options = tableOptions(snapshot);
  const showBet = !snapshot.anim && options.some((o) => o.id === 'bet');
  // Every game with a bet keeps the bet row on screen (locked mid-play), so
  // the controls are one height in every state.
  const hasBet = !GAMES[id].fixedBet;
  const buttons: ActionSpec[] = [
    ...options.filter((o) => !['bet', 'waiter', 'skip', ...hide].includes(o.id)).map((o) => ({ id: o.id, label: o.label })),
    ...(extra ?? []),
  ];
  const primary = buttons.find((b) => b.primary || b.id === primaryId) ?? buttons[buttons.length === 2 && !showBet ? 1 : 0];
  return (
    <footer className="controls">
      {hasBet && (
        <BetControl
          bet={run.tables[id]?.bet ?? 0}
          ladder={betLadder(id, run.floor).filter((b) => b <= run.bankroll)}
          onChange={actions.setBet}
          locked={!showBet}
        />
      )}
      <div className="control-buttons" style={{ ['--count' as string]: Math.max(1, buttons.length) }}>
        {buttons.map((b) => (
          <button
            key={b.id}
            className={`btn ${b === primary ? 'btn-primary' : 'btn-secondary'}`}
            disabled={!!snapshot.anim || b.id === 'wait'}
            onClick={() => {
              onAct?.();
              if (b.onClick) b.onClick();
              else actions.tableOption(b.id);
            }}
          >
            <ButtonLabel label={b.label} />
          </button>
        ))}
      </div>
    </footer>
  );
}

/**
 * A control label as the action with its detail underneath, smaller:
 * "Higher" over "×1.15", "Cash out" over "$1,250", "Bid" over "3 × 6s".
 * Every button holds two lines, so a label never gets cut off and the
 * controls never change height (user, 2026-09-27).
 */
function ButtonLabel({ label }: { label: string }) {
  const text = withCommas(label).replace(/\bx(?=\d)/g, '×');
  const m = text.match(/^(.+?)\s+((?:×\d|\$|\(|\d+ ×).*)$/);
  if (!m) return <span className="btn-main">{text}</span>;
  return (
    <>
      <span className="btn-main">{m[1]}</span>
      <span className="btn-sub">{m[2]!.replace(/^\((.*)\)$/, '$1')}</span>
    </>
  );
}

/** Milliseconds since the current animation started (0 when none). A web
 *  animation ends itself (actions.skip) once `total` has passed. */
export function useAnimClock(snapshot: GameSnapshot, actions: GameActions, total: number): number {
  const [elapsed, setElapsed] = useState(0);
  const frames = snapshot.anim?.frames;
  useEffect(() => {
    setElapsed(0);
    if (!frames) return;
    const start = performance.now();
    const t = setInterval(() => setElapsed(performance.now() - start), 40);
    return () => clearInterval(t);
  }, [frames]);
  useEffect(() => {
    if (frames && elapsed >= total) actions.skip();
  }, [frames, elapsed, total, actions]);
  return frames ? elapsed : 0;
}

// ── The ★ second-chance starburst ──

/** How long the starburst stays, then how long it takes to fade (user,
 *  2026-09-27: it shouldn't linger until the next move). */
const BURST_SHOW_MS = 3600;
const FADE_MS = 600;

export interface SecondChance {
  /** What luck did, in a few words. */
  what: string;
  /** Whether it turned the result around (null: not known yet). */
  saved: boolean | null;
}

/** Keeps a second chance on screen for a while after it happens, then fades it. */
export function useBurst(): [SecondChance | null, boolean, (c: SecondChance | null) => void] {
  const [burst, setBurst] = useState<SecondChance | null>(null);
  const [fading, setFading] = useState(false);
  useEffect(() => {
    setFading(false);
    if (!burst) return;
    const fade = setTimeout(() => setFading(true), BURST_SHOW_MS);
    const gone = setTimeout(() => setBurst(null), BURST_SHOW_MS + FADE_MS);
    return () => {
      clearTimeout(fade);
      clearTimeout(gone);
    };
  }, [burst]);
  return [burst, fading, setBurst];
}

export function Burst({ burst, fading, pending }: { burst: SecondChance | null; fading: boolean; pending?: boolean }) {
  if (!burst) return null;
  const outcome = pending || burst.saved === null ? '' : burst.saved ? 'Saved.' : 'No luck this time.';
  return (
    <div className={`burst${burst.saved === false && !pending ? ' failed' : ''}${fading ? ' fading' : ''}`} role="status">
      <span className="burst-title">★ Second chance!</span>
      <span className="burst-sub">{burst.what} {outcome}</span>
    </div>
  );
}

/**
 * Shows the starburst at the moment luck steps in, not when the animation
 * starts: the losing result plays first, then the second chance (user,
 * 2026-09-27). `at` is that moment, in ms into the animation (null: no
 * second chance this play).
 */
export function useLuckMoment(snapshot: GameSnapshot, elapsed: number, at: number | null, make: () => SecondChance, setBurst: (c: SecondChance | null) => void) {
  const fired = useRef<string[] | undefined>(undefined);
  const frames = snapshot.anim?.frames;
  useEffect(() => {
    if (!frames || at === null || fired.current === frames || elapsed < at) return;
    fired.current = frames;
    setBurst(make());
  }, [frames, elapsed, at]); // eslint-disable-line react-hooks/exhaustive-deps
}

/** How long the full-screen BOOM stays up (matches .big-boom in games.css). */
const BIG_BOOM_MS = 1300;

/**
 * A full-screen BOOM! over the whole table when something blows up (Mines,
 * Crash; user, 2026-09-27), in the second-chance starburst's shape. Give it
 * a new non-null `trigger` to set it off once; it clears itself.
 */
export function BigBoom({ trigger }: { trigger: unknown }) {
  const seen = useRef<unknown>(null);
  const [on, setOn] = useState(0);
  useEffect(() => {
    if (trigger === null || trigger === undefined || trigger === seen.current) return;
    seen.current = trigger;
    setOn(Date.now());
  }, [trigger]);
  // Its own timer, so the trigger going back to null doesn't cancel it.
  useEffect(() => {
    if (!on) return;
    const t = setTimeout(() => setOn(0), BIG_BOOM_MS);
    return () => clearTimeout(t);
  }, [on]);
  if (!on) return null;
  return (
    <div className="big-boom" key={on} role="status" aria-label="Boom">
      <div className="big-boom-rim" />
      <div className="big-boom-core">
        <span>BOOM!</span>
      </div>
    </div>
  );
}

const JACKPOT_MS = 2600;

/**
 * A progressive jackpot win fills the screen in gold (the glasses play the
 * engine's JACKPOT frames; the web tables draw their own animations, so this
 * is the web's celebration). Shows once the table's own animation is over,
 * when the run's jackpot count goes up.
 */
export function JackpotSplash({ snapshot }: { snapshot: GameSnapshot }) {
  const won = snapshot.run?.stats.jackpotsWon ?? 0;
  const shown = useRef(won);
  const [on, setOn] = useState<{ at: number; amount: string } | null>(null);
  useEffect(() => {
    if (snapshot.anim || won <= shown.current) return;
    shown.current = won;
    const amount = snapshot.notice?.match(/JACKPOT! \+\$([\d,]+)/)?.[1];
    setOn({ at: Date.now(), amount: amount ? `+$${Number(amount.replace(/,/g, '')).toLocaleString('en-US')}` : '' });
  }, [won, snapshot.anim, snapshot.notice]);
  useEffect(() => {
    if (!on) return;
    const t = setTimeout(() => setOn(null), JACKPOT_MS);
    return () => clearTimeout(t);
  }, [on]);
  if (!on) return null;
  return (
    <div className="jackpot-splash" key={on.at} role="status" aria-label={`Jackpot ${on.amount}`}>
      <div className="big-boom-rim" />
      <div className="big-boom-core">
        <span>JACKPOT!</span>
        {on.amount && <b>{on.amount}</b>}
      </div>
    </div>
  );
}

/** The frame an engine animation marks as the ★'s second chance, if any. */
export function secondChanceFrame(snapshot: GameSnapshot): string | undefined {
  return snapshot.anim?.frames.find((f) => f.includes('Second chance'));
}
